import Vue from "vue";
import Vuex, { Store, StoreOptions } from "vuex";

import { createCompEventTeamStore } from "./athleteCompSched/comp-event-teams/comp-event-store";
import { createAthleteCompSchedStore } from "./athleteCompSched/store/athleteCompSched-store";
import { createAuthStore } from "./auth/auth-store";
import { builderStore, createBuilderStore } from "./builder/builder-store";
import { createCartStore } from "./cart/cart-store";
import { createClubStore } from "./club/club-store";
import { createConfigStore } from "./config/config-store";
import { createEntryStore } from "./entry/entry-store";
import {
  CLUB_STORE_CONST,
  CONFIG_STORE_CONST,
  ENTRY_STORE_CONST,
  USER_MESSAGE_STORE_CONST,
  AUTH_STORE_CONST,
  TEAM_STORE_CONST,
  CART_STORE_CONST,
  BUILDER_STORE_CONST,
  ATH_COMP_SCHED_STORE_CONST,
  COMP_EVENT_TEAMS_STORE_CONST,
  PUBLIC_COMPS_STORE_CONST,
  PERMISSION_STORE_CONST,
  FIELD_HELP_STORE_CONST,
  CHECKIN_STORE_CONST,
  SCOREBOARD_STORE_CONST,
  SECONDARY_STORE_CONST,
  SECONDARY_CUST_STORE_CONST,
  RESULTS_STORE_CONST,
  SCHEDULE_COMP_PUBLIC_STORE_CONST,
} from "./store/store-constants";
import { createTeamStore } from "./team/team-store";
import { createUserMessageStore } from "./user-message/user-message-store";
import { createPublicCompsStore } from "./public/entry-public/public-comps-store";
import { createPermissionStore } from "./admin/permissions/permission-store";
import {
  createFieldHelpStore,
  fieldHelpStore,
} from "./common/ui/field/field-help/field-help-store";
import {
  checkInStore,
  createCheckinStore,
} from "./competition/checkin/checkin-store";
import { createScoreboardStore } from "./competition/scoreboard/scorboard-store";
import { createSecondaryStore } from "./secondary/secondary-store";
import { createSecondaryCustStore } from "./secondary/cust/secondary-cust-store";
import { createResultsStore } from "./competition/results/results-store";
import { createScheduleCompPublicStore } from "./competition/results/public/schedule/schedule-comp-public-store";
import { initializeServices } from "./common/services/service-registry";

Vue.use(Vuex);

// Import IRootState from separate file to avoid circular dependencies
import type { IRootState } from "./store/root-state";
export type { IRootState };

/**
 * Create the application store using the factory pattern
 * This replaces the immediate instantiation with lazy loading
 */
function createAppStore(): Store<IRootState> {
  try {
    // Initialize services first to avoid circular dependencies
    // But do it in a try-catch to handle any initialization issues gracefully
    let serviceContainer;
    try {
      serviceContainer = initializeServices();
    } catch (serviceError) {
      console.warn(
        "[AppStore] Service initialization failed, continuing with store creation:",
        serviceError
      );
      // For now, continue without services - they might be initialized later
      serviceContainer = null;
    }

    // Create store with all modules - services will be initialized later if needed
    return new Vuex.Store<IRootState>({
      strict: true,
      modules: {
        [CLUB_STORE_CONST.CLUB_STORE_CONST_MODULE_NAME]: createClubStore(serviceContainer),
        [USER_MESSAGE_STORE_CONST.USER_MESSAGE_CONST_MODULE_NAME]:
          createUserMessageStore(serviceContainer),
        [ATH_COMP_SCHED_STORE_CONST.ATH_COMP_SCHED_CONST_MODULE_NAME]:
          createAthleteCompSchedStore(serviceContainer),
        [CART_STORE_CONST.CART_CONST_MODULE_NAME]: createCartStore(serviceContainer),
        [AUTH_STORE_CONST.AUTH_CONST_MODULE_NAME]: createAuthStore(serviceContainer),
        [ENTRY_STORE_CONST.ENTRY_STORE_CONST_MODULE_NAME]: createEntryStore(serviceContainer),
        [TEAM_STORE_CONST.TEAM_STORE_CONST_MODULE_NAME]:
          createTeamStore(serviceContainer),
        [CONFIG_STORE_CONST.CONFIG_CONST_MODULE_NAME]: createConfigStore(serviceContainer),
        [BUILDER_STORE_CONST.BUILDER_CONST_MODULE_NAME]:
          createBuilderStore(serviceContainer),
        [COMP_EVENT_TEAMS_STORE_CONST.COMP_EVENT_TEAMS_CONST_MODULE_NAME]:
          createCompEventTeamStore(serviceContainer),
        [PUBLIC_COMPS_STORE_CONST.PUBLIC_COMPS_CONST_MODULE_NAME]:
          createPublicCompsStore(serviceContainer),
        [PERMISSION_STORE_CONST.PERMISSION_CONST_MODULE_NAME]: createPermissionStore(serviceContainer),
        [FIELD_HELP_STORE_CONST.CONST_MODULE_NAME]:
          createFieldHelpStore(serviceContainer),
        [CHECKIN_STORE_CONST.CHECKIN_CONST_MODULE_NAME]:
          createCheckinStore(serviceContainer),
        [SCOREBOARD_STORE_CONST.SCOREBOARD_CONST_MODULE_NAME]: createScoreboardStore(serviceContainer),
        [SECONDARY_STORE_CONST.SECONDARY_CONST_MODULE_NAME]: createSecondaryStore(serviceContainer),
        [SECONDARY_CUST_STORE_CONST.SECONDARY_CUST_CONST_MODULE_NAME]:
          createSecondaryCustStore(serviceContainer),
        [RESULTS_STORE_CONST.RESULTS_CONST_MODULE_NAME]: createResultsStore(serviceContainer),
        [SCHEDULE_COMP_PUBLIC_STORE_CONST.SCHEDULE_COMP_PUBLIC_MODULE_NAME]:
          createScheduleCompPublicStore(serviceContainer),
      },
    });
  } catch (error) {
    console.error("[AppStore] Failed to create application store:", error);
    throw new Error(`Store creation failed: ${error}`);
  }
}

// HMR-safe store initialization using global variable approach
// This avoids module-level state issues during hot reload
declare global {
  var __E4S_STORE_INSTANCE__: Store<IRootState> | null;
}

// Initialize global store instance if it doesn't exist
if (typeof globalThis !== 'undefined' && !globalThis.__E4S_STORE_INSTANCE__) {
  globalThis.__E4S_STORE_INSTANCE__ = null;
}

function getStoreInstance(): Store<IRootState> {
  if (!globalThis.__E4S_STORE_INSTANCE__) {
    globalThis.__E4S_STORE_INSTANCE__ = createAppStore();
  }
  return globalThis.__E4S_STORE_INSTANCE__;
}

// Export for HMR compatibility - use function instead of constant
export function getAppStore(): Store<IRootState> {
  return getStoreInstance();
}

// For backward compatibility
export const appStore = getStoreInstance();

export interface IAppStoreDispatchHelper {
  actionName: string;
  dataProp: string;
  loadingProp: string;
}

//  Until v3, use this
export function useStore(): Store<IRootState> {
  return appStore;
}
